// ⚠️ BARE folder imports, and that is REQUIRED here — hazard 16's `shared.ts` rule. Both symbols
// are platform-defined (`setLocalStorage` is browser localStorage on web and react-native-mmkv on
// mobile; `LOCALSTORAGE_KEYS` is a separate literal in each variant), so this file MUST let each
// bundler pick. Naming a variant here would hard-wire one platform's storage into the other.
// `LOCALSTORAGE_KEYS.defaultPrinter` was checked to exist in BOTH variants before relying on it.
import { LOCALSTORAGE_KEYS } from "../../../utils/constants";
import { setLocalStorage } from "../../../utils/localStorage";
import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of PrinterSlice — plan §5.2. **10 of 13 reducers are shared**, the highest ratio
 * on the branch so far: both apps drive the same printer list with the same bodies, and the
 * storage call inside `clearDefaultPrinter` resolves per platform through the imports above.
 *
 * What diverges:
 * - **`updatePrinterSlice`** — web carries a one-time migration off the old manual-localStorage
 *   format (`OLD_STATE_KEY`) plus a `defaultPrinterDetails` -> `defaultPrinter` rename; mobile
 *   just stores the payload. Real behaviour, per platform.
 * - **`setDefaultPrinter` (web) vs `updateDefaultPrinter` (mobile)** — ⚠️ **byte-identical
 *   bodies under DIFFERENT NAMES.** They are different ACTION TYPES
 *   (`printer/setDefaultPrinter` vs `printer/updateDefaultPrinter`), dispatched by each app's own
 *   sagas and components, so they cannot be merged without renaming an action on one platform.
 *   Left as-is: this is a naming divergence, not a behavioural one, and renaming is out of scope
 *   for a behaviour-preserving uplift. Worth a follow-up ticket.
 * - **`localPrinters` + `saveLocalPrintersLocally`** — web-only state and its only writer.
 * - web's `extraReducers` — see the note in `index.desktop.ts`; the bodies are EMPTY.
 *
 * ⚠️ `printerStore` is in BOTH apps' redux-persist whitelists. No state SHAPE changes here, so
 * no persist migration is involved.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger.
 */
export interface SharedPrinterState {
  allPrinters: any;
  isLoading: boolean;
  defaultPrinter: any;
}

export const sharedInitialState: SharedPrinterState = {
  allPrinters: [],
  isLoading: false,
  defaultPrinter: {},
};

/** Identical in both apps. */
export const sharedReducers = {
  savePrinter: (state: SharedPrinterState, action: PayloadAction<any>) => {},
  storeAllPrinters: (state: SharedPrinterState, action: PayloadAction<any>) => {
    // if(getLocalStorage('usbPrinter')){
    //   action.payload = [...action.payload,JSON.parse(getLocalStorage('usbPrinter') || '')]
    // }
    if (state.allPrinters?.length) {
      state.allPrinters = state?.allPrinters
        .concat(action.payload)
        .sort(
          (a: any, b: any) =>
            -a.lastModifiedDate.localeCompare(b.lastModifiedDate)
        );
    } else {
      state.allPrinters = action.payload;
    }
  },
  addUSBPrinter : (state: SharedPrinterState, action: PayloadAction<any>) =>{
    state.allPrinters = [...state?.allPrinters, action?.payload]
  },
  addLocalPrinter : (state: SharedPrinterState, action: PayloadAction<any>) =>{
  },
  fetchAllPrinter: (state: SharedPrinterState, action: PayloadAction<any>) => {},
  deletePrinter: (state: SharedPrinterState, action: PayloadAction<any>) => {},
  emptyPrinters: (state: SharedPrinterState, action: PayloadAction<any>) => {
    state.allPrinters = [];
  },
  clearDefaultPrinter: (state: SharedPrinterState) => {
    state.defaultPrinter = {};
    setLocalStorage(LOCALSTORAGE_KEYS.defaultPrinter, "{}"); // TODO: to be removed once the default printer issue is fixed.
  },
  getUnprintedTickets: (state: SharedPrinterState, action: PayloadAction<any>) => {},
  getLabelPrintStatus: (state: SharedPrinterState, action: PayloadAction<any>) => {},
};
